/**
 * FTPEPendingBanner - Liste déroulante des adhésions FTPE en cours
 * Affiche les demandes non finalisées (30 jours) au-dessus du tableau des membres
 * Réutilisable dans tous les espaces: Super Admin, Admin, Cooper, Expert
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { RefreshCw, PlayCircle, FileEdit, CreditCard, FileSignature, AlertTriangle, ChevronDown, Clock } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_MAP = {
  brouillon: { label: 'Brouillon', icon: FileEdit, bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' },
  en_attente_signature: { label: 'Signature', icon: FileSignature, bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
  en_attente_paiement: { label: 'Paiement', icon: CreditCard, bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  en_attente_virement: { label: 'Virement', icon: Clock, bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' },
};

function DaysBar({ days }) {
  const pct = Math.min(100, (days / 30) * 100);
  const color = days > 20 ? 'bg-emerald-500' : days > 10 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = days > 20 ? 'text-emerald-700' : days > 10 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="flex items-center gap-1.5">
      <div className="h-1.5 w-12 rounded-full bg-gray-200 overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", color)} style={{ width: `${pct}%` }} />
      </div>
      <span className={cn("text-[11px] font-semibold tabular-nums", textColor)}>{days}j</span>
    </div>
  );
}

function StatusTag({ status }) {
  const cfg = STATUS_MAP[status] || STATUS_MAP.brouillon;
  const Icon = cfg.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border", cfg.bg, cfg.text, cfg.border)}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

export default function FTPEPendingBanner({ getAuthHeader }) {
  const navigate = useNavigate();
  const [adhesions, setAdhesions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => { fetchPending(); }, []);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ftpe-adhesion/workflow/pending-adhesions`, {
        headers: getAuthHeader()
      });
      setAdhesions(response.data.pending_adhesions || []);
    } catch {
      setAdhesions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResume = (adhesion) => {
    if (adhesion.type === 'draft') {
      navigate(`/adhesion-ftpe-workflow?draft_id=${adhesion.id}`);
    } else {
      navigate(`/adhesion-ftpe-workflow?adhesion_id=${adhesion.id}&step=payment`);
    }
  };

  if (adhesions.length === 0 && !loading) return null;

  return (
    <div className="relative" data-testid="ftpe-pending-banner">
      {/* Trigger bar */}
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all",
          "hover:shadow-sm cursor-pointer select-none",
          open
            ? "bg-amber-50 border-amber-300 shadow-sm"
            : "bg-white border-amber-200 hover:border-amber-300"
        )}
        data-testid="ftpe-pending-toggle"
      >
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium text-amber-900">Adhesions FTPE en cours</span>
          <Badge className="bg-amber-600 text-white text-[10px] px-1.5 py-0 font-bold">{adhesions.length}</Badge>
          <span className="text-[11px] text-amber-500 hidden sm:inline">— Demandes non finalisees (30 jours)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); fetchPending(); }}
            className="h-6 w-6 inline-flex items-center justify-center rounded text-amber-500 hover:bg-amber-100 transition-colors"
            data-testid="ftpe-pending-refresh"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </span>
          <ChevronDown className={cn("h-4 w-4 text-amber-500 transition-transform duration-200", open && "rotate-180")} />
        </div>
      </button>

      {/* Dropdown list */}
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded-lg border border-amber-200 bg-white shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
          <div className="max-h-[340px] overflow-y-auto divide-y divide-gray-100">
            {adhesions.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between px-4 py-2.5 hover:bg-amber-50/50 transition-colors"
                data-testid={`ftpe-pending-row-${a.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 text-sm truncate" title={a.raison_sociale}>
                      {a.raison_sociale || 'Non renseigne'}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StatusTag status={a.status} />
                      <span className="text-[10px] text-gray-400">{a.territoire || ''}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-amber-700 tabular-nums">{a.montant_cotisation || 200}&euro;</span>
                    <DaysBar days={a.days_remaining} />
                  </div>
                </div>
                <div className="ml-3 shrink-0">
                  {a.can_resume && (
                    <Button
                      size="sm"
                      onClick={() => handleResume(a)}
                      className="h-7 bg-amber-600 hover:bg-amber-700 text-[11px] px-2"
                      data-testid={`ftpe-resume-${a.id}`}
                    >
                      <PlayCircle className="h-3 w-3 mr-1" />
                      Reprendre
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close when clicking outside */}
      {open && (
        <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
      )}
    </div>
  );
}
